import { useCallback } from 'react';
import { useFilter } from '../context/FilterContext';
import { useSurebet } from '../../surebets/context/SurebetContext';
import FilterService from '../services/FilterService';

/**
 * useFilterIntegration - Custom hook for integrating filter context with surebet context
 * 
 * This hook connects the filter state to the surebet data fetching,
 * allowing filter changes to trigger data refreshes with the selected bookmakers.
 * 
 * @returns {Object} - Integration utilities and actions
 */
export const useFilterIntegration = () => {
  const {
    selectedBookmakers,
    isApplyingFilters,
    filterError,
    applyFilters: applyFiltersBase,
    selectAllBookmakers,
    deselectAllBookmakers,
    resetFilters,
    selectedBookmakerCount,
    totalBookmakerCount,
    hasActiveFilters,
  } = useFilter();

  const {
    loadSurebets,
    updateBookmakers,
    loading: surebetsLoading,
    error: surebetsError,
  } = useSurebet();

  /**
   * Apply filters and refresh surebet data
   */
  const applyFiltersAndRefresh = useCallback(async () => {
    console.log('🔍 useFilterIntegration: Applying filters and refreshing data...');
    
    try {
      // Use FilterService to validate and format parameters
      const filterParams = {
        selectedBookmakers,
        sport: null,
        championship: null,
        profitableOnly: true,
      };

      // Validate filter combinations
      const validation = FilterService.validateFilterCombinations(filterParams);
      if (!validation.isValid) {
        throw new Error(`Invalid filter combination: ${validation.errors.join(', ')}`);
      }

      // Handle empty selection
      const emptySelection = FilterService.handleEmptySelection(selectedBookmakers);
      if (emptySelection.isEmpty) {
        console.log('📝 useFilterIntegration: Empty selection detected, using fallback');
        const fallbackBookmakers = emptySelection.fallbackBookmakers || Object.keys(require('../../surebets/constants/surebetTypes').BOOKMAKERS);
        updateBookmakers(fallbackBookmakers);
        const result = await loadSurebets(fallbackBookmakers);
        return { success: true, data: result.data, warning: emptySelection.message };
      }

      // Format API parameters
      const apiResult = FilterService.formatApiParameters(filterParams);
      if (!apiResult.success) {
        throw new Error(`Failed to format API parameters: ${apiResult.error}`);
      }

      // Update bookmakers in surebet context
      updateBookmakers(selectedBookmakers);
      
      // Load surebets with new bookmaker filter
      const result = await loadSurebets(selectedBookmakers);
      
      console.log('✅ useFilterIntegration: Filters applied successfully');
      
      return { success: true, data: result.data };
    } catch (error) {
      console.error('❌ useFilterIntegration: Error applying filters:', error);
      return { success: false, error: error.message };
    }
  }, [selectedBookmakers, updateBookmakers, loadSurebets]);

  /**
   * Reset filters and refresh with all bookmakers
   */
  const resetFiltersAndRefresh = useCallback(async () => {
    console.log('🔍 useFilterIntegration: Resetting filters and refreshing data...');
    
    try {
      // Reset to all bookmakers
      resetFilters();
      
      // Load all surebets (empty array means all bookmakers in backend)
      const allBookmakerIds = Object.keys(require('../../surebets/constants/surebetTypes').BOOKMAKERS);
      const result = await loadSurebets(allBookmakerIds);
      
      console.log('✅ useFilterIntegration: Filters reset successfully');
      
      return { success: true, data: result.data };
    } catch (error) {
      console.error('❌ useFilterIntegration: Error resetting filters:', error);
      return { success: false, error: error.message };
    }
  }, [resetFilters, loadSurebets]);

  /**
   * Quick select all bookmakers and refresh
   */
  const selectAllAndRefresh = useCallback(async () => {
    console.log('🔍 useFilterIntegration: Selecting all bookmakers and refreshing...');
    
    selectAllBookmakers();
    return await applyFiltersAndRefresh();
  }, [selectAllBookmakers, applyFiltersAndRefresh]);

  /**
   * Check if filters can be applied (validation)
   */
  const canApplyFilters = selectedBookmakers.length > 0;

  return {
    // State
    selectedBookmakers,
    isApplyingFilters: isApplyingFilters || surebetsLoading,
    filterError: filterError || surebetsError,
    selectedBookmakerCount,
    totalBookmakerCount,
    hasActiveFilters,
    canApplyFilters,
    
    // Actions
    applyFiltersAndRefresh,
    resetFiltersAndRefresh,
    selectAllAndRefresh,
  };
};

export default useFilterIntegration;

